import React, { useEffect, useState } from "react";
import { Star, Check, X, Trash2, Save } from "lucide-react";
import api, { eur } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

export function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const load = () => api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const moderate = async (r, approved) => { await api.put(`/admin/reviews/${r.id}?approved=${approved}`); load(); toast.success(approved ? "Εγκρίθηκε" : "Απορρίφθηκε"); };
  const del = async (id) => { await api.delete(`/admin/reviews/${id}`); load(); };
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Reviews" subtitle="ΑΞΙΟΛΟΓΗΣΕΙΣ" />
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} className="card-surface rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-[#FF2E4C] text-[#FF2E4C]" : "text-zinc-600"}`} />)}</div>
                <span className="text-xs font-semibold">{r.user_name}</span>
                {r.verified_purchase && <span className="text-[10px] font-mono text-green-500">✓ VERIFIED</span>}
                <span className={`text-[10px] font-mono uppercase ${r.approved ? "text-green-500" : "text-yellow-500"}`}>{r.approved ? "Εγκεκριμένη" : "Σε αναμονή"}</span>
              </div>
              <div className="flex gap-1">
                <button onClick={() => moderate(r, !r.approved)} className={`p-1.5 ${r.approved ? "text-yellow-500" : "text-green-500"}`} data-testid={`review-toggle-${r.id}`}>{r.approved ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}</button>
                <button onClick={() => del(r.id)} className="p-1.5 text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-sm text-zinc-300">{r.text}</p>
          </div>
        ))}
        {reviews.length === 0 && <p className="text-zinc-500 text-sm">Δεν υπάρχουν αξιολογήσεις.</p>}
      </div>
    </div>
  );
}

export function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {}); }, []);
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Customers" subtitle="ΠΕΛΑΤΕΣ" />
      <div className="card-surface rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="text-left text-zinc-500 font-mono text-xs uppercase border-b border-zinc-800"><th className="p-4">Όνομα</th><th className="p-4">Email</th><th className="p-4">Τηλέφωνο</th><th className="p-4">Παραγγελίες</th><th className="p-4">Σύνολο</th></tr></thead>
          <tbody className="divide-y divide-zinc-800">
            {customers.map((c) => <tr key={c.user_id}><td className="p-4 font-semibold">{c.name}</td><td className="p-4 text-zinc-400">{c.email}</td><td className="p-4 text-zinc-400">{c.phone || "-"}</td><td className="p-4">{c.order_count}</td><td className="p-4 font-semibold">{eur(c.total_spent)}</td></tr>)}
          </tbody>
        </table>
        {customers.length === 0 && <p className="p-8 text-center text-zinc-500 text-sm">Δεν υπάρχουν πελάτες ακόμα.</p>}
      </div>
    </div>
  );
}

export function AdminAnalytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a) return <div className="p-10"><div className="w-8 h-8 border-2 border-[#FF2E4C] border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Analytics" subtitle="ΣΤΑΤΙΣΤΙΚΑ" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[["Σήμερα", a.today_sales], ["Εβδομάδα", a.weekly_sales], ["Μήνας", a.monthly_sales], ["Σύνολο", a.total_sales]].map(([l, v]) => (
          <div key={l} className="card-surface rounded-lg p-5"><p className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">{l}</p><p className="font-display text-2xl font-black">{eur(v)}</p></div>
        ))}
      </div>
      <div className="card-surface rounded-lg p-6 mb-6">
        <h3 className="font-display font-bold uppercase text-sm mb-6">Έσοδα 7 ημερών</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={a.revenue_series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
            <XAxis dataKey="date" stroke="#71717A" fontSize={11} /><YAxis stroke="#71717A" fontSize={11} />
            <Tooltip contentStyle={{ background: "#18181C", border: "1px solid #27272A", borderRadius: 8 }} formatter={(v) => eur(v)} cursor={{ fill: "rgba(255,46,76,0.1)" }} />
            <Bar dataKey="revenue" fill="#FF2E4C" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-surface rounded-lg p-5"><p className="text-[11px] font-mono uppercase text-zinc-500 mb-1">Παραγγελίες</p><p className="font-display text-3xl font-black">{a.order_count}</p></div>
        <div className="card-surface rounded-lg p-5"><p className="text-[11px] font-mono uppercase text-zinc-500 mb-1">Μ.Ο. Παραγγελίας</p><p className="font-display text-3xl font-black">{eur(a.aov)}</p></div>
        <div className="card-surface rounded-lg p-5"><p className="text-[11px] font-mono uppercase text-zinc-500 mb-1">Πελάτες</p><p className="font-display text-3xl font-black">{a.customers}</p></div>
      </div>
    </div>
  );
}

const inp = "w-full bg-[#0A0A0B] border border-zinc-700 rounded px-3 py-2.5 text-sm outline-none focus:border-[#FF2E4C]";

function ImageField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success("Η εικόνα ανέβηκε");
    } catch { toast.error("Αποτυχία ανεβάσματος"); } finally { setUploading(false); }
  };
  return (
    <div>
      <label className="text-xs font-mono uppercase text-zinc-500">{label}</label>
      <div className="mt-1 border border-dashed border-zinc-700 rounded p-3">
        {value && <img src={value} alt="" className="w-full h-28 object-cover rounded mb-2" />}
        <label className="cursor-pointer inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#FF2E4C]">
          <Save className="w-4 h-4" /> {uploading ? "..." : "Ανέβασμα"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={testid} />
        </label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="ή URL" className={`${inp} mt-2 text-xs`} />
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState({ announcement: "", instagram: "", tiktok: "", contact_email: "", free_shipping_threshold: 80, hero_title: "", hero_subtitle: "", hero_image: "", banner_image: "", banner_eyebrow: "", banner_title: "", banner_subtitle: "", banner_link: "", drop_end: "" });
  useEffect(() => { api.get("/settings").then(({ data }) => setS((prev) => ({ ...prev, ...data }))).catch(() => {}); }, []);
  const save = async () => { await api.put("/admin/settings", { ...s, free_shipping_threshold: parseFloat(s.free_shipping_threshold) || 0 }); toast.success("Οι ρυθμίσεις αποθηκεύτηκαν"); };
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const setV = (k) => (v) => setS((prev) => ({ ...prev, [k]: v }));
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Settings" subtitle="ΡΥΘΜΙΣΕΙΣ" />
      <div className="space-y-6 max-w-2xl">
        <div className="card-surface rounded-lg p-6 space-y-4">
          <h3 className="font-display font-bold uppercase text-sm">Γενικά</h3>
          <div><label className="text-xs font-mono uppercase text-zinc-500">Announcement Bar</label><input value={s.announcement} onChange={set("announcement")} className={inp} data-testid="set-announcement" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-mono uppercase text-zinc-500">Instagram URL</label><input value={s.instagram} onChange={set("instagram")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">TikTok URL</label><input value={s.tiktok} onChange={set("tiktok")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Email Επικοινωνίας</label><input value={s.contact_email} onChange={set("contact_email")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Δωρεάν Μεταφορικά από (€)</label><input type="number" value={s.free_shipping_threshold} onChange={set("free_shipping_threshold")} className={inp} data-testid="set-threshold" /></div>
          </div>
        </div>

        <div className="card-surface rounded-lg p-6 space-y-4">
          <h3 className="font-display font-bold uppercase text-sm">Hero (Αρχική)</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-mono uppercase text-zinc-500">Hero Title</label><input value={s.hero_title} onChange={set("hero_title")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Hero Subtitle</label><input value={s.hero_subtitle} onChange={set("hero_subtitle")} className={inp} /></div>
          </div>
          <ImageField label="Hero Εικόνα" value={s.hero_image} onChange={setV("hero_image")} testid="set-hero-image" />
        </div>

        <div className="card-surface rounded-lg p-6 space-y-4">
          <h3 className="font-display font-bold uppercase text-sm">Campaign Banner + Drop Countdown</h3>
          <ImageField label="Banner Εικόνα" value={s.banner_image} onChange={setV("banner_image")} testid="set-banner-image" />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-mono uppercase text-zinc-500">Eyebrow</label><input value={s.banner_eyebrow} onChange={set("banner_eyebrow")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Link (π.χ. /shop?badge=limited)</label><input value={s.banner_link} onChange={set("banner_link")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Τίτλος</label><input value={s.banner_title} onChange={set("banner_title")} className={inp} /></div>
            <div><label className="text-xs font-mono uppercase text-zinc-500">Υπότιτλος</label><input value={s.banner_subtitle} onChange={set("banner_subtitle")} className={inp} /></div>
          </div>
          <div><label className="text-xs font-mono uppercase text-zinc-500">Drop Countdown λήξη (ημ/νία & ώρα)</label><input type="datetime-local" value={s.drop_end} onChange={set("drop_end")} className={inp} data-testid="set-drop-end" /><p className="text-[10px] text-zinc-500 mt-1">Άφησέ το κενό για να κρύψεις το countdown.</p></div>
        </div>

        <button onClick={save} className="btn-red px-6 py-3 text-xs font-bold uppercase rounded flex items-center gap-2" data-testid="set-save"><Save className="w-4 h-4" /> ΑΠΟΘΗΚΕΥΣΗ</button>
      </div>
    </div>
  );
}
